import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';

import { displayUrl } from '/src/utils/url';

import { adPlatformAdPreviewQuery } from '../queries';

const OVERLAY_WIDTH = 360;
const DEFAULT_OVERLAY_HEIGHT = 540;
const VIEWPORT_PADDING = 16;
const GAP_FROM_TRIGGER = 10;
const ARROW_SIZE = 12;

export function AdPreviewOverlay({
  siteId,
  adId,
  fallbackAdName,
  anchorRect,
  onMouseEnter = () => {},
  onMouseLeave = () => {},
}) {
  const overlayRef = useRef(null);
  const [overlayPosition, setOverlayPosition] = useState(null);
  const { data, error, isLoading, isFetching } = useQuery({
    ...adPlatformAdPreviewQuery({
      siteId,
      adId,
    }),
  });
  const payload = data?.adPlatformAdPreview;
  const preview = payload?.preview;
  const ad = payload?.ad;

  useLayoutEffect(() => {
    if (!anchorRect || typeof window === 'undefined') {
      return;
    }

    const updatePosition = () => {
      const measuredDimensions = measureOverlayDimensions(overlayRef.current);

      setOverlayPosition(getOverlayPosition(anchorRect, measuredDimensions));
    };

    updatePosition();

    if (!window.ResizeObserver || !overlayRef.current) {
      return undefined;
    }

    const resizeObserver = new window.ResizeObserver(updatePosition);
    resizeObserver.observe(overlayRef.current);

    return () => resizeObserver.disconnect();
  }, [
    ad?.name,
    anchorRect,
    error,
    isFetching,
    isLoading,
    preview?.body,
    preview?.descriptions,
    preview?.headline,
    preview?.headlines,
    preview?.imageUrl,
    preview?.mediaType,
    preview?.path1,
    preview?.path2,
    preview?.template,
    preview?.videoUrl,
  ]);

  if (typeof document === 'undefined' || !anchorRect) {
    return null;
  }

  const position = overlayPosition || {
    arrowStyle: { visibility: 'hidden' },
    overlayStyle: {
      left: VIEWPORT_PADDING,
      top: VIEWPORT_PADDING,
      visibility: 'hidden',
    },
  };

  return createPortal(
    <div
      ref={overlayRef}
      data-testid="ad-preview-overlay"
      className="fixed z-[200] w-[360px]"
      style={position.overlayStyle}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div
        className="absolute -left-1.5 h-3 w-3 rotate-45 border border-mystic-500 border-b-0 border-r-0 bg-white"
        style={position.arrowStyle}
      />
      <div
        className={[
          'overflow-hidden rounded-2xl border border-mystic-500 bg-white',
          'shadow-[0_18px_48px_rgba(21,35,60,0.2)]',
        ].join(' ')}
      >
        {isLoading || isFetching ? (
          <OverlayState title={fallbackAdName} body="Loading preview..." />
        ) : error ? (
          <OverlayState title={fallbackAdName} body="We couldn't load this preview right now." tone="error" />
        ) : !preview?.previewSupported ? (
          <OverlayState title={ad?.name || fallbackAdName} body="Preview is not available for this platform yet." />
        ) : preview.template === 'META_FEED' ? (
          <MetaFeedPreview ad={ad} preview={preview} />
        ) : preview.template === 'GOOGLE_SEARCH' ? (
          <GoogleSearchPreview ad={ad} preview={preview} />
        ) : preview.template === 'GOOGLE_DISPLAY' ? (
          <GoogleDisplayPreview ad={ad} preview={preview} />
        ) : preview.template === 'GOOGLE_VIDEO' ? (
          <GoogleVideoPreview ad={ad} preview={preview} />
        ) : preview.template === 'REDDIT_FEED' ? (
          <RedditFeedPreview ad={ad} preview={preview} />
        ) : (
          <OverlayState title={ad?.name || fallbackAdName} body="Preview is not available for this template yet." />
        )}
      </div>
    </div>,
    document.body,
  );
}

function OverlayState({ title, body, tone = 'default' }) {
  return (
    <div className="p-5">
      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-lynch-500">Ad Preview</p>
      <h3 className="mt-3 text-base font-semibold text-black-pearl-500">{title}</h3>
      <p className={`mt-3 text-sm ${tone === 'error' ? 'text-red-700' : 'text-lynch-500'}`}>{body}</p>
    </div>
  );
}

function MetaFeedPreview({ ad, preview }) {
  const primaryText = preview.body || preview.headline || ad?.name;
  const headline = preview.headline || ad?.name;
  const resolvedDisplayUrl = preview.displayUrl || (preview.destinationUrl ? displayUrl(preview.destinationUrl) : null);
  const advertiserName = preview.advertiserName || ad?.name || 'Facebook ad';

  return (
    <article aria-label={`${ad?.name || advertiserName} preview`} className="bg-white">
      <div className="border-b border-solitude-500 px-5 py-4">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-lynch-500">
          {preview.placementLabel || 'Facebook Feed'}
        </p>
      </div>

      <div className="px-5 py-4">
        <div className="flex items-start gap-3">
          <div
            className={[
              'flex h-11 w-11 shrink-0 items-center justify-center rounded-full',
              'bg-solitude-500 text-sm font-semibold text-[#00509D]',
            ].join(' ')}
          >
            {(advertiserName || 'A').slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="truncate text-[15px] font-semibold text-black-pearl-500">{advertiserName}</div>
            <div className="mt-0.5 text-xs text-lynch-500">Sponsored</div>
            {ad?.campaignName ? <div className="mt-1 text-xs text-lynch-500">{ad.campaignName}</div> : null}
          </div>
        </div>

        {primaryText ? (
          <p
            className="mt-4 text-[15px] leading-6 text-black-pearl-500"
            style={{
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 4,
              overflow: 'hidden',
            }}
          >
            {primaryText}
          </p>
        ) : null}
      </div>

      <PreviewMedia
        preview={preview}
        alt={headline || advertiserName}
        iframeTitle={`${headline || advertiserName} video preview`}
        emptyState="No synced media is available for this creative yet."
        className="max-h-[320px] w-full object-cover"
        emptyStateClassName="h-[220px] bg-solitude-500 text-lynch-500"
      />

      <div className="border-t border-solitude-500 bg-white-lilac-500 px-5 py-4">
        <div className="text-[11px] uppercase tracking-[0.12em] text-lynch-500">
          {resolvedDisplayUrl || 'Destination URL'}
        </div>
        <div className="mt-2 text-sm font-semibold text-black-pearl-500">{headline || 'Untitled creative'}</div>
        {preview.destinationUrl ? (
          <div className="mt-2 truncate text-sm text-[#00509D]">{preview.destinationUrl}</div>
        ) : null}
      </div>
    </article>
  );
}

function GoogleSearchPreview({ ad, preview }) {
  const headlines = preview.headlines?.length
    ? preview.headlines.slice(0, 3)
    : [preview.headline || ad?.name].filter(Boolean);
  const descriptions = preview.descriptions?.length ? preview.descriptions.slice(0, 2) : [preview.body].filter(Boolean);
  const resolvedDisplayUrl = preview.displayUrl || (preview.destinationUrl ? displayUrl(preview.destinationUrl) : null);

  return (
    <article aria-label={`${ad?.name || 'Google ad'} preview`} className="bg-[#F8FAFD]">
      <div className="border-b border-solitude-500 bg-white px-5 py-4">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-lynch-500">
          {preview.placementLabel || 'Google Search'}
        </p>
      </div>

      <div className="px-5 py-5">
        <div className="rounded-2xl border border-[#E3E9F5] bg-white px-4 py-4 shadow-[0_10px_24px_rgba(21,35,60,0.08)]">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#1A73E8] text-sm font-semibold text-white">
              {(ad?.name || 'G').slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold text-black-pearl-500">
                {resolvedDisplayUrl || 'googlead.example'}
              </div>
              <div className="mt-0.5 text-xs text-lynch-500">Sponsored</div>
            </div>
          </div>

          {headlines.length ? (
            <div className="mt-4 text-[22px] leading-8 text-[#1A0DAB]">
              {headlines.map((headline, index) => (
                <span key={`${headline}-${index}`}>
                  {index > 0 ? <span className="mx-2 text-[#5F6368]">|</span> : null}
                  {headline}
                </span>
              ))}
            </div>
          ) : null}

          {resolvedDisplayUrl ? <div className="mt-2 text-sm text-[#188038]">{resolvedDisplayUrl}</div> : null}

          {descriptions.length ? (
            <div className="mt-3 space-y-1 text-[15px] leading-6 text-[#4D5156]">
              {descriptions.map((description, index) => (
                <p key={`${description}-${index}`}>{description}</p>
              ))}
            </div>
          ) : (
            <div className="mt-3 text-sm text-lynch-500">No synced copy is available for this search ad yet.</div>
          )}
        </div>
      </div>
    </article>
  );
}

function GoogleDisplayPreview({ ad, preview }) {
  const headline = preview.headline || ad?.name || preview.displayUrl || 'Display creative';
  const body =
    preview.body || preview.descriptions?.[0] || 'Preview copy is not available for this display creative yet.';
  const advertiserName = preview.advertiserName || preview.displayUrl || 'Google Display';
  const resolvedDisplayUrl = preview.displayUrl || (preview.destinationUrl ? displayUrl(preview.destinationUrl) : null);

  return (
    <article aria-label={`${ad?.name || 'Google display ad'} preview`} className="bg-[#F8FAFD]">
      <div className="border-b border-solitude-500 bg-white px-5 py-4">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-lynch-500">
          {preview.placementLabel || 'Google Display'}
        </p>
      </div>

      <div className="px-5 py-5">
        <div className="overflow-hidden rounded-2xl border border-[#E3E9F5] bg-white shadow-[0_10px_24px_rgba(21,35,60,0.08)]">
          <div className="px-4 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#E8F0FE] text-sm font-semibold text-[#1A73E8]">
                Ad
              </div>
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-black-pearl-500">{advertiserName}</div>
                <div className="mt-0.5 text-xs text-lynch-500">Google Ads</div>
              </div>
            </div>
          </div>

          <PreviewMedia
            preview={preview}
            alt={headline}
            iframeTitle={`${headline} media preview`}
            emptyState="No synced image or video is available for this display creative yet."
            className="h-[220px] w-full object-cover"
            emptyStateClassName="h-[220px] bg-[#EEF3FB] text-[#5F6368]"
          />

          <div className="px-4 py-4">
            {resolvedDisplayUrl ? <div className="text-xs text-[#188038]">{resolvedDisplayUrl}</div> : null}
            <div className="mt-2 text-lg font-semibold leading-7 text-black-pearl-500">{headline}</div>
            <p className="mt-2 text-[15px] leading-6 text-[#4D5156]">{body}</p>
          </div>
        </div>
      </div>
    </article>
  );
}

function GoogleVideoPreview({ ad, preview }) {
  const headline = preview.headline || ad?.name || preview.displayUrl || 'Video ad';
  const body = preview.body || preview.descriptions?.[0] || 'Preview copy is not available for this video ad yet.';
  const advertiserName = preview.advertiserName || preview.displayUrl || 'YouTube ad';
  const resolvedDisplayUrl = preview.displayUrl || (preview.destinationUrl ? displayUrl(preview.destinationUrl) : null);

  return (
    <article aria-label={`${ad?.name || 'Google video ad'} preview`} className="bg-[#0F1115]">
      <div className="border-b border-[#20242C] px-5 py-4">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#B3BDCC]">
          {preview.placementLabel || 'YouTube'}
        </p>
      </div>

      <div className="px-5 py-5">
        <div className="overflow-hidden rounded-2xl border border-[#20242C] bg-[#181C24] shadow-[0_18px_44px_rgba(0,0,0,0.35)]">
          <PreviewMedia
            preview={preview}
            alt={headline}
            iframeTitle={`${headline} video preview`}
            emptyState="No synced video asset is available for this creative yet."
            className="aspect-video w-full bg-black object-cover"
            emptyStateClassName="aspect-video bg-black text-[#B3BDCC]"
          />

          <div className="px-4 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#FF0033] text-xs font-semibold uppercase text-white">
                YT
              </div>
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-white">{advertiserName}</div>
                <div className="mt-0.5 text-xs text-[#B3BDCC]">Sponsored video</div>
              </div>
            </div>

            <div className="mt-4 text-[17px] font-semibold leading-7 text-white">{headline}</div>
            <p className="mt-2 text-sm leading-6 text-[#D6DBE4]">{body}</p>
            {resolvedDisplayUrl ? <div className="mt-3 text-sm text-[#7EE787]">{resolvedDisplayUrl}</div> : null}
          </div>
        </div>
      </div>
    </article>
  );
}

function RedditFeedPreview({ ad, preview }) {
  const advertiserName = preview.advertiserName || 'u/promoted';
  const headline = preview.headline || ad?.name || 'Promoted post';
  const resolvedDisplayUrl = preview.displayUrl || (preview.destinationUrl ? displayUrl(preview.destinationUrl) : null);

  return (
    <article aria-label={`${ad?.name || advertiserName} preview`} className="bg-[#F6F7F8]">
      <div className="border-b border-solitude-500 bg-white px-5 py-4">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-lynch-500">
          {preview.placementLabel || 'Reddit Feed'}
        </p>
      </div>

      <div className="px-5 py-5">
        <div className="overflow-hidden rounded-2xl border border-[#D7DADF] bg-white shadow-[0_10px_24px_rgba(21,35,60,0.08)]">
          <div className="px-4 py-4">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#FF4500] text-sm font-semibold text-white">
                {(advertiserName || 'R').slice(0, 1).toUpperCase()}
              </div>
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-black-pearl-500">{advertiserName}</div>
                <div className="mt-0.5 text-xs text-lynch-500">Promoted</div>
              </div>
            </div>

            <div className="mt-4 text-[18px] font-semibold leading-7 text-[#1A1A1B]">{headline}</div>
            {preview.body ? <p className="mt-2 text-[15px] leading-6 text-[#1A1A1B]">{preview.body}</p> : null}
          </div>

          <PreviewMedia
            preview={preview}
            alt={headline}
            iframeTitle={`${headline} media preview`}
            emptyState="No synced media is available for this promoted post yet."
            className="max-h-[280px] w-full object-cover"
            emptyStateClassName="h-[220px] bg-[#F2F4F5] text-[#6A6F77]"
          />

          {resolvedDisplayUrl ? (
            <div className="border-t border-[#E9EBED] px-4 py-3 text-sm text-[#6A6F77]">{resolvedDisplayUrl}</div>
          ) : null}
        </div>
      </div>
    </article>
  );
}

function PreviewMedia({ preview, alt, iframeTitle, emptyState, className = '', emptyStateClassName = '' }) {
  const youtubeEmbedUrl = getYouTubeEmbedUrl(preview.videoUrl);

  if (youtubeEmbedUrl) {
    return (
      <div className={['w-full bg-black', className].join(' ')}>
        <iframe
          title={iframeTitle}
          className="h-full w-full border-0"
          src={youtubeEmbedUrl}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }

  if (preview.mediaType === 'VIDEO' && preview.videoUrl) {
    return <video className={['bg-black', className].join(' ')} controls muted playsInline src={preview.videoUrl} />;
  }

  if (preview.mediaType === 'IMAGE' && preview.imageUrl) {
    return <img className={className} alt={alt} src={preview.imageUrl} />;
  }

  return (
    <div className={['flex items-center justify-center px-6 text-center text-sm', emptyStateClassName].join(' ')}>
      {emptyState}
    </div>
  );
}

function getYouTubeEmbedUrl(url) {
  if (!url) return null;

  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace(/^www\./u, '');

    if (host === 'youtube.com' || host === 'm.youtube.com') {
      const videoId = parsed.searchParams.get('v');
      return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
    }

    if (host === 'youtu.be') {
      const videoId = parsed.pathname.replace(/^\/+/u, '');
      return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
    }
  } catch {
    return null;
  }

  return null;
}

function getOverlayPosition(anchorRect, { height, width }) {
  const viewportWidth = window.innerWidth || width + VIEWPORT_PADDING * 2;
  const viewportHeight = window.innerHeight || height + VIEWPORT_PADDING * 2;

  const left = clamp(
    anchorRect.right + GAP_FROM_TRIGGER,
    VIEWPORT_PADDING,
    Math.max(VIEWPORT_PADDING, viewportWidth - width - VIEWPORT_PADDING),
  );
  const top = clamp(
    anchorRect.top + anchorRect.height / 2 - height / 2,
    VIEWPORT_PADDING,
    Math.max(VIEWPORT_PADDING, viewportHeight - height - VIEWPORT_PADDING),
  );
  const arrowTop = clamp(
    anchorRect.top + anchorRect.height / 2 - top - ARROW_SIZE / 2,
    VIEWPORT_PADDING,
    Math.max(VIEWPORT_PADDING, height - VIEWPORT_PADDING - ARROW_SIZE),
  );

  return {
    overlayStyle: {
      left,
      top,
      visibility: 'visible',
    },
    arrowStyle: {
      top: arrowTop,
    },
  };
}

function measureOverlayDimensions(node) {
  const rect = node?.getBoundingClientRect?.() || {};

  return {
    width: rect.width || OVERLAY_WIDTH,
    height: rect.height || DEFAULT_OVERLAY_HEIGHT,
  };
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}
